import React, { useState, useEffect } from 'react';
import { Dog, Cat, PawPrint, Syringe, Stethoscope, Calendar, LogOut, AlertCircle, Clock, CalendarClock, Weight, X, TrendingUp, TrendingDown, Minus, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getCustomerPets, getCustomerOperations, getCustomerUpcoming, getCustomerClinicInfo, getCustomerPetWeightHistory } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import Loader from '../components/Loader';
import PetAvatar from '../components/PetAvatar';
import PetIcon from '../components/PetIcon';
import CustomerAppointments from '../components/customer/CustomerAppointments';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_140853a5-d37e-4724-8f64-c2f7eabb8728/artifacts/4msp2ofk_agrosvetlogo.png';

const getSpeciesLabel = (species) => {
  switch (species) {
    case 'dog': return `Köpek`;
    case 'cat': return 'Kedi';
    case 'bird': return `Kuş`;
    default: return `Diğer`;
  }
};

const CustomerPanel = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('pets');
  const [pets, setPets] = useState([]);
  const [operations, setOperations] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [clinicInfo, setClinicInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weightModal, setWeightModal] = useState(null); // { petId, petName }
  const [weightHistory, setWeightHistory] = useState([]);
  const [weightLoading, setWeightLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [petsRes, opsRes, upcomingRes, clinicRes] = await Promise.all([
        getCustomerPets(),
        getCustomerOperations(),
        getCustomerUpcoming(),
        getCustomerClinicInfo()
      ]);
      setPets(petsRes.data);
      setOperations(opsRes.data);
      setUpcoming(upcomingRes.data);
      setClinicInfo(clinicRes.data);
    } catch (error) {
      console.error('Error loading customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openWeightHistory = async (petId, petName) => {
    setWeightModal({ petId, petName });
    setWeightLoading(true);
    try {
      const res = await getCustomerPetWeightHistory(petId);
      setWeightHistory(res.data || []);
    } catch {
      setWeightHistory([]);
    } finally {
      setWeightLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'vaccine': return <Syringe size={16} className="text-green-600" />;
      case 'treatment': return <Stethoscope size={16} className="text-blue-600" />;
      default: return <Calendar size={16} className="text-orange-600" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'vaccine': return `Aşı`;
      case 'treatment': return 'Tedavi';
      case 'service': return 'Hizmet';
      default: return type;
    }
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-8" data-testid="customer-panel">
      {/* Header */}
      <header className="bg-white px-4 pt-12 pb-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="AGROSVET" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-lg font-bold text-gray-900 font-['Plus_Jakarta_Sans']">AGROSVET</h1>
              <p className="text-xs text-[#8E8E93]">{`Merhaba, ${user?.name}`}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="text-[#8E8E93] text-sm"
            data-testid="customer-logout-btn"
          >
            <LogOut size={18} className="mr-1" />
            {`Çıkış`}
          </Button>
        </div>

        {/* Clinic Info */}
        {clinicInfo?.clinic_name && (
          <div className="flex items-center gap-2 px-3 py-2 bg-[#F2F2F7] rounded-xl mb-3" data-testid="clinic-info-bar">
            <Building2 size={16} className="text-[#6b6de3] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{clinicInfo.clinic_name}</p>
              {(clinicInfo.clinic_city || clinicInfo.clinic_phone) && (
                <p className="text-xs text-[#8E8E93] truncate">
                  {[clinicInfo.clinic_city, clinicInfo.clinic_phone].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex bg-[#F2F2F7] rounded-xl p-1 gap-1">
          {[
            { key: 'pets', label: `Petlerim`, icon: PawPrint },
            { key: 'appointments', label: `Randevularım`, icon: CalendarClock },
            { key: 'upcoming', label: `Hatırlatmalar`, icon: AlertCircle },
            { key: 'history', label: `Geçmiş`, icon: Clock },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2.5 rounded-lg flex items-center justify-center gap-1.5 text-xs font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-white text-[#6b6de3] shadow-sm'
                  : 'text-[#8E8E93]'
              }`}
              data-testid={`customer-tab-${tab.key}`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <CustomerAppointments pets={pets} />
        )}

        {/* Pets Tab */}
        {activeTab === 'pets' && (
          <div className="space-y-3 animate-fade-in">
            {pets.length > 0 ? (
              pets.map((pet) => (
                <div key={pet.id} className="ios-card" data-testid={`customer-pet-${pet.id}`}>
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center">
                      <PetAvatar species={pet.species} gender={pet.gender} size={30} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-gray-900 text-lg">{pet.name}</h3>
                      <p className="text-sm text-[#8E8E93]">{getSpeciesLabel(pet.species)}{pet.breed ? ` · ${pet.breed}` : ''}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs font-medium text-[#8E8E93]">Cinsiyet:</span>
                      <span className="text-sm text-gray-900">{pet.gender ? (pet.gender === 'male' ? 'Erkek' : 'Dişi') : <span className="text-[#C7C7CC]">Bilinmiyor</span>}</span>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs font-medium text-[#8E8E93]">Renk:</span>
                      <span className="text-sm text-gray-900">{pet.color || <span className="text-[#C7C7CC]">Bilinmiyor</span>}</span>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs font-medium text-[#8E8E93]">Doğum:</span>
                      <span className="text-sm text-gray-900">{pet.birth_date ? <>
                        {new Date(pet.birth_date + 'T00:00:00').toLocaleDateString('tr-TR')}
                        <span className="text-[#8E8E93] ml-1">({(() => {
                          const bd = new Date(pet.birth_date + 'T00:00:00');
                          const now = new Date();
                          let years = now.getFullYear() - bd.getFullYear();
                          let months = now.getMonth() - bd.getMonth();
                          if (now.getDate() < bd.getDate()) months--;
                          if (months < 0) { years--; months += 12; }
                          const parts = [];
                          if (years > 0) parts.push(`${years} yaş`);
                          if (months > 0) parts.push(`${months} ay`);
                          return parts.length > 0 ? parts.join(' ') : 'Yeni doğan';
                        })()})</span>
                      </> : <span className="text-[#C7C7CC]">Bilinmiyor</span>}</span>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs font-medium text-[#8E8E93]">Ağırlık:</span>
                      <span className="text-sm text-gray-900">{pet.weight_grams > 0 ? (pet.weight_grams >= 1000 ? `${(pet.weight_grams / 1000).toFixed(1)} kg` : `${pet.weight_grams} g`) : <span className="text-[#C7C7CC]">Bilinmiyor</span>}</span>
                      <button
                        onClick={() => openWeightHistory(pet.id, pet.name)}
                        className="ml-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#6b6de3]/10 text-[#6b6de3] hover:bg-[#6b6de3]/20 transition-colors"
                        data-testid={`weight-history-btn-${pet.id}`}
                      >
                        Kilo Geçmişi
                      </button>
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xs font-medium text-[#8E8E93]">Kısır:</span>
                      {pet.is_neutered === true ? <span className="text-sm font-medium text-green-600">Evet</span>
                        : pet.is_neutered === false ? <span className="text-sm font-medium text-red-500">Hayır</span>
                        : <span className="text-sm text-[#C7C7CC]">Bilinmiyor</span>}
                    </div>
                    {pet.microchip_no && (
                      <div className="flex items-baseline gap-1.5 col-span-2">
                        <span className="text-xs font-medium text-[#8E8E93]">Mikroçip:</span>
                        <span className="text-sm text-gray-900">{pet.microchip_no}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="ios-card text-center py-8">
                <PawPrint size={48} className="text-[#C7C7CC] mx-auto mb-3" />
                <p className="text-[#8E8E93]">{`Henüz kayıtlı petiniz yok`}</p>
              </div>
            )}
          </div>
        )}

        {/* Upcoming Tab */}
        {activeTab === 'upcoming' && (
          <div className="space-y-3 animate-fade-in">
            {upcoming.length > 0 ? (
              upcoming.map((item) => (
                <div key={item.id} className="ios-card" data-testid={`upcoming-item-${item.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center`}>
                        <PetAvatar species={item.pet_species} gender={item.pet_gender} size={22} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{item.pet_name}</p>
                        <div className="flex items-center gap-1.5">
                          {getTypeIcon(item.operation_type)}
                          <p className="text-sm text-[#8E8E93]">{item.operation_name}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-[#FF9500]">
                        {new Date(item.next_due_date).toLocaleDateString('tr-TR')}
                      </p>
                      <p className="text-xs text-[#8E8E93]">{getTypeLabel(item.operation_type)}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="ios-card text-center py-8">
                <AlertCircle size={48} className="text-[#C7C7CC] mx-auto mb-3" />
                <p className="text-[#8E8E93]">{`Yaklaşan hatırlatma yok`}</p>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-3 animate-fade-in">
            {operations.length > 0 ? (
              operations.map((op) => (
                <div key={op.id} className="ios-card" data-testid={`history-item-${op.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getTypeIcon(op.type)}
                      <div>
                        <p className="font-medium text-gray-900">{op.name}</p>
                        <p className="text-xs text-[#8E8E93]">{op.pet_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-900">
                        {new Date(op.date).toLocaleDateString('tr-TR')}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        op.type === 'vaccine' ? 'bg-green-100 text-green-600' :
                        op.type === 'treatment' ? 'bg-blue-100 text-blue-600' :
                        'bg-orange-100 text-orange-600'
                      }`}>
                        {getTypeLabel(op.type)}
                      </span>
                    </div>
                  </div>
                  {op.note && (
                    <p className="text-xs text-[#8E8E93] mt-2 pt-2 border-t border-gray-100">{op.note}</p>
                  )}
                </div>
              ))
            ) : (
              <div className="ios-card text-center py-8">
                <Clock size={48} className="text-[#C7C7CC] mx-auto mb-3" />
                <p className="text-[#8E8E93]">{`Henüz işlem geçmişi yok`}</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Weight History Modal */}
      {weightModal && (
        <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="weight-history-modal">
          <div className="absolute inset-0 bg-black/40" onClick={() => setWeightModal(null)} />
          <div className="relative w-full sm:max-w-md lg:max-w-3xl bg-white sm:rounded-2xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-2">
                <Weight size={18} className="text-[#6b6de3]" />
                <h3 className="font-semibold text-gray-900">{weightModal.petName} - Kilo Geçmişi</h3>
              </div>
              <button onClick={() => setWeightModal(null)} className="p-1.5 hover:bg-gray-100 rounded-full">
                <X size={18} className="text-gray-400" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {weightLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : weightHistory.length === 0 ? (
                <div className="text-center py-8 text-[#8E8E93]">
                  <Weight size={36} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Kilo kaydı bulunamadı</p>
                </div>
              ) : (
                weightHistory.map((rec, i) => {
                  const prev = weightHistory[i + 1];
                  const diff = prev ? rec.weight_grams - prev.weight_grams : 0;
                  const wKg = rec.weight_grams >= 1000 ? `${(rec.weight_grams / 1000).toFixed(1)} kg` : `${rec.weight_grams} g`;
                  return (
                    <div key={rec.id || i} className="flex items-center justify-between px-3 py-2.5 bg-[#F8F8FA] rounded-xl" data-testid={`weight-record-${i}`}>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{wKg}</p>
                        <p className="text-xs text-[#8E8E93]">{rec.created_at ? new Date(rec.created_at).toLocaleDateString('tr-TR') : ''}</p>
                        {rec.text && <p className="text-xs text-[#8E8E93] mt-0.5">{rec.text}</p>}
                      </div>
                      {diff !== 0 && (
                        <div className={`flex items-center gap-0.5 text-xs font-medium ${diff > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {diff > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                          {diff > 0 ? '+' : ''}{diff >= 1000 || diff <= -1000 ? `${(diff / 1000).toFixed(1)} kg` : `${diff} g`}
                        </div>
                      )}
                      {diff === 0 && prev && (
                        <div className="flex items-center gap-0.5 text-xs font-medium text-[#8E8E93]">
                          <Minus size={14} />
                          Değişim yok
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPanel;
